/**
 * Typed wrapper for the citizen liveness check endpoints on
 * service-kyc (port 3090).
 *
 * Endpoints:
 *   - POST /v1/kyc/liveness/start  — start a liveness check; auto-passes after 2s
 *   - GET  /v1/kyc/liveness/:id    — fetch status (auto-completes if 2s elapsed)
 *
 * RBAC: Kyc/liveness-start-own — granted to the `customer` role by
 *       20260509_nipost_pay_destub_perms.sql.
 */
import { requestJson, type RequestJsonOptions } from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export interface LivenessCheck {
  readonly id: string;
  readonly status: 'PENDING' | 'PASSED' | 'FAILED';
  readonly score: number | null;
  readonly vendor: string;
  readonly vendorRef: string | null;
  readonly createdAt: string;
  readonly completedAt: string | null;
}

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

export async function startLivenessCheck(
  options: CallOpts = {},
): Promise<LivenessCheck> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<LivenessCheck>({
    method: 'POST',
    baseUrl: env.kycBaseUrl,
    path: '/v1/kyc/liveness/start',
    body: {},
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function getLivenessCheck(
  id: string,
  options: CallOpts = {},
): Promise<LivenessCheck> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<LivenessCheck>({
    method: 'GET',
    baseUrl: env.kycBaseUrl,
    path: `/v1/kyc/liveness/${encodeURIComponent(id)}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}
